import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiRequest } from '../lib/api';
import { formatVnDate, formatVnDateTime } from '../lib/dateUtils';

export interface DeviceHistoryModalProps {
  deviceId: string;
  initialData?: any;
  onClose: () => void;
  showToast?: (msg: string, type: 'info' | 'error' | 'warning') => void;
}

interface TimelineItem {
  id: string;
  timestamp: number;
  timeStr: string;
  icon: string;
  iconBg: string;
  tag: string;
  tagColor: string;
  tagBg: string;
  title: string;
  description: string;
  extra?: string | null;
}

export default function DeviceHistoryModal({ deviceId, initialData, onClose, showToast }: DeviceHistoryModalProps) {
  const [data, setData] = useState<any>(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      setLoading(true);
      try {
        const res = await apiRequest<any>(`/device/${deviceId}/history`, undefined, true);
        if (isMounted) setData(res);
      } catch (err: any) {
        if (showToast) showToast(err.message || 'Lỗi tải lịch sử thiết bị', 'error');
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchData();
    return () => { isMounted = false; };
  }, [deviceId]);

  function copyUid(uid: string) {
    navigator.clipboard.writeText(uid);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  // Build unified timeline
  const timeline: TimelineItem[] = [];

  if (data?.device) {
    const d = data.device;
    const createdDate = new Date(d.createdAt);
    timeline.push({
      id: 'event-created',
      timestamp: createdDate.getTime(),
      timeStr: formatVnDateTime(d.createdAt),
      icon: '📦',
      iconBg: 'rgba(56, 189, 248, 0.15)',
      tag: 'Khởi tạo / Xuất xưởng',
      tagColor: '#38bdf8',
      tagBg: 'rgba(56, 189, 248, 0.1)',
      title: 'Thiết bị được tạo trên hệ thống',
      description: `Model: ${d.model} · Phân phối: ${d.source === 'SHOP' ? 'Shop 3D Smart' : 'Đối tác ngoài'} · Bảo hành cam kết: ${d.warrantyMonths ? `${d.warrantyMonths} tháng` : 'Không bảo hành'}`,
    });
  }

  if (data?.warrantyRecords) {
    data.warrantyRecords.forEach((w: any) => {
      const actDate = new Date(w.activatedAt);
      const isAuto = w.activationReason === 'AUTO_10_DAYS';
      timeline.push({
        id: `event-warranty-${w.id}`,
        timestamp: actDate.getTime(),
        timeStr: formatVnDateTime(w.activatedAt),
        icon: isAuto ? '🤖' : '🛡️',
        iconBg: isAuto ? 'rgba(245, 158, 11, 0.15)' : 'rgba(34, 197, 94, 0.15)',
        tag: isAuto ? 'Tự động kích hoạt (XK + 3 ngày)' : 'Kích hoạt thủ công',
        tagColor: isAuto ? '#fbbf24' : '#4ade80',
        tagBg: isAuto ? 'rgba(245, 158, 11, 0.1)' : 'rgba(34, 197, 94, 0.1)',
        title: isAuto ? 'Hệ thống tự động kích hoạt bảo hành sau 10 ngày xuất kho' : 'Người dùng kích hoạt bảo hành chính hãng',
        description: isAuto
          ? `Bắt đầu tính bảo hành từ: ${formatVnDate(w.activatedAt)} (Xuất kho + 3 ngày) · Hạn đến: ${formatVnDate(w.expiresAt)}`
          : `Bắt đầu tính bảo hành từ: ${formatVnDate(w.activatedAt)} · Hạn đến: ${formatVnDate(w.expiresAt)}`,
        extra: w.activationNote || null,
      });
    });
  }

  if (data?.ownerships) {
    data.ownerships.forEach((o: any) => {
      const startDate = new Date(o.startAt);
      timeline.push({
        id: `event-ownership-${o.id}`,
        timestamp: startDate.getTime(),
        timeStr: formatVnDateTime(o.startAt),
        icon: '👤',
        iconBg: 'rgba(16, 185, 129, 0.15)',
        tag: o.isActive ? 'Chủ sở hữu hiện tại' : 'Chủ sở hữu cũ',
        tagColor: o.isActive ? '#34d399' : '#94a3b8',
        tagBg: o.isActive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(148, 163, 184, 0.1)',
        title: `Liên kết quyền sở hữu: ${o.user.name || o.user.email}`,
        description: `Email: ${o.user.email}${o.endAt ? ` · Đã kết thúc sở hữu vào ${formatVnDate(o.endAt)}` : ' · Đang kích hoạt sử dụng'}`,
      });
    });
  }

  if (data?.auditLogs) {
    data.auditLogs.forEach((l: any) => {
      // Avoid duplicate display for auto-warranty if already listed in warrantyRecords
      if (l.action === 'WARRANTY_AUTO_ACTIVATED' || l.action === 'DEVICE_ACTIVATED_BY_USER') return;

      const logDate = new Date(l.createdAt);
      let title = l.action;
      let tag = 'Nhật ký hệ thống';
      let icon = '⚡';
      let iconBg = 'rgba(148, 163, 184, 0.15)';
      let tagColor = '#94a3b8';
      let tagBg = 'rgba(148, 163, 184, 0.1)';
      let description = '';

      if (l.action === 'DEVICE_TRANSFERRED') {
        icon = '🔄';
        iconBg = 'rgba(168, 85, 247, 0.15)';
        tag = 'Chuyển nhượng';
        tagColor = '#c084fc';
        tagBg = 'rgba(168, 85, 247, 0.1)';
        title = 'Chuyển giao quyền sở hữu thiết bị';
        description = `Chuyển giao từ ${l.metadata?.fromEmail || 'Người dùng'} sang ${l.metadata?.toEmail || 'Người dùng mới'}.`;
      } else if (l.action === 'DEVICE_UNLINKED') {
        icon = '🔓';
        iconBg = 'rgba(239, 68, 68, 0.15)';
        tag = 'Hủy liên kết';
        tagColor = '#f87171';
        tagBg = 'rgba(239, 68, 68, 0.1)';
        title = 'Hủy liên kết thiết bị khỏi tài khoản';
        description = `Thực hiện bởi: ${l.metadata?.unlinkedBy || 'Người dùng'}.`;
      }

      timeline.push({
        id: `event-audit-${l.id}`,
        timestamp: logDate.getTime(),
        timeStr: formatVnDateTime(l.createdAt),
        icon,
        iconBg,
        tag,
        tagColor,
        tagBg,
        title,
        description,
      });
    });
  }

  if (data?.tickets) {
    data.tickets.forEach((t: any) => {
      const ticketDate = new Date(t.createdAt);
      const isClosed = t.status === 'closed';
      const isOpen = t.status === 'open';
      timeline.push({
        id: `event-ticket-${t.id}`,
        timestamp: ticketDate.getTime(),
        timeStr: formatVnDateTime(t.createdAt),
        icon: '🛠️',
        iconBg: 'rgba(234, 179, 8, 0.15)',
        tag: isClosed ? 'Hỗ trợ: Hoàn tất' : isOpen ? 'Hỗ trợ: Chờ xử lý' : 'Hỗ trợ: Đang xử lý',
        tagColor: isClosed ? '#4ade80' : isOpen ? '#fbbf24' : '#38bdf8',
        tagBg: isClosed ? 'rgba(34, 197, 94, 0.1)' : isOpen ? 'rgba(245, 158, 11, 0.1)' : 'rgba(56, 189, 248, 0.1)',
        title: `Phiếu hỗ trợ: ${t.title}`,
        description: t.description || 'Không có ghi chú thêm',
      });
    });
  }

  // Sort descending (newest first)
  timeline.sort((a, b) => b.timestamp - a.timestamp);

  const device = data?.device;
  const currentOwner = data?.ownerships?.find((o: any) => o.isActive);
  const latestWarranty = data?.warrantyRecords?.[0];

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0d111a', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '20px', width: '100%', maxWidth: '680px', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px -15px rgba(0,0,0,0.9)',
          overflow: 'hidden', color: '#f8fafc',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 28px', borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%)',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🕰️</span> Lịch sử vòng đời thiết bị
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#94a3b8' }}>
              Toàn bộ tiến trình xuất xưởng, kích hoạt bảo hành, chuyển nhượng và hỗ trợ kỹ thuật
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '50%',
              width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '18px', cursor: 'pointer', color: '#94a3b8', transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            &times;
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }} className="custom-scrollbar">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
              <p>Đang tải dữ liệu vòng đời thiết bị...</p>
            </div>
          ) : !data ? (
            <p style={{ textAlign: 'center', color: '#f87171', padding: '32px 0' }}>Không thể tải thông tin thiết bị.</p>
          ) : (
            <>
              {/* Device Summary Card */}
              {device && (
                <div style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '16px', padding: '18px 22px', marginBottom: '28px',
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px',
                }}>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                      Device UID
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '15px', color: '#38bdf8' }}>
                        {device.deviceUid}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyUid(device.deviceUid)}
                        style={{
                          background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '6px',
                          padding: '2px 6px', fontSize: '11px', color: copied ? '#4ade80' : '#cbd5e1', cursor: 'pointer',
                        }}
                      >
                        {copied ? '✓ Đã chép' : 'Sao chép'}
                      </button>
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                      Model: <strong style={{ color: '#e2e8f0' }}>{device.model}</strong>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                      Chủ sở hữu hiện tại
                    </div>
                    {currentOwner ? (
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '14px', color: '#f8fafc' }}>
                          {currentOwner.user.name || currentOwner.user.email}
                        </div>
                        <div style={{ fontSize: '12px', color: '#34d399' }}>
                          ● Đang liên kết
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize: '13px', color: '#facc15', fontStyle: 'italic' }}>
                        Chưa có chủ sở hữu
                      </span>
                    )}
                  </div>

                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                      Trạng thái bảo hành
                    </div>
                    {device.warrantyMonths === 0 ? (
                      <span style={{ fontSize: '13px', color: '#94a3b8' }}>Không áp dụng bảo hành</span>
                    ) : latestWarranty ? (
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: new Date(latestWarranty.expiresAt) > new Date() ? '#4ade80' : '#f87171' }}>
                          {new Date(latestWarranty.expiresAt) > new Date()
                            ? `Còn hạn đến ${formatVnDate(latestWarranty.expiresAt)}`
                            : `Hết hạn (${formatVnDate(latestWarranty.expiresAt)})`}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                          {latestWarranty.activationReason === 'AUTO_10_DAYS' ? '🤖 Tự động (XK + 3 ngày)' : '👤 Kích hoạt thủ công'}
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize: '13px', color: '#facc15' }}>
                        Chưa kích hoạt
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Timeline Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#e2e8f0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Dòng thời gian sự kiện ({timeline.length})
                </h4>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Mới nhất xếp trên cùng</span>
              </div>

              {/* Timeline List */}
              {timeline.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#64748b', padding: '24px 0' }}>Chưa có sự kiện nào được ghi nhận.</p>
              ) : (
                <div style={{ position: 'relative', paddingLeft: '32px' }}>
                  {/* Vertical Line */}
                  <div style={{
                    position: 'absolute', left: '13px', top: '12px', bottom: '12px',
                    width: '2px', background: 'linear-gradient(180deg, rgba(56, 189, 248, 0.4) 0%, rgba(255, 255, 255, 0.05) 100%)',
                  }} />

                  {timeline.map((event, idx) => (
                    <div key={event.id} style={{ position: 'relative', marginBottom: idx === timeline.length - 1 ? 0 : '24px' }}>
                      {/* Node Icon */}
                      <div style={{
                        position: 'absolute', left: '-32px', top: '0px', width: '28px', height: '28px',
                        borderRadius: '50%', background: event.iconBg, border: '2px solid #0d111a',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px',
                        boxShadow: '0 0 10px rgba(0,0,0,0.5)', zIndex: 2,
                      }}>
                        {event.icon}
                      </div>

                      {/* Event Content Card */}
                      <div style={{
                        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                        borderRadius: '12px', padding: '14px 18px', transition: 'all 0.15s',
                      }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <span style={{
                            fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px',
                            background: event.tagBg, color: event.tagColor,
                          }}>
                            {event.tag}
                          </span>
                          <span style={{ fontSize: '12px', color: '#64748b', fontFamily: 'monospace' }}>
                            {event.timeStr}
                          </span>
                        </div>

                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#f8fafc', marginBottom: '4px' }}>
                          {event.title}
                        </div>

                        <div style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: '1.5' }}>
                          {event.description}
                        </div>

                        {event.extra && (
                          <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', marginTop: '6px', borderTop: '1px dashed rgba(255,255,255,0.06)', paddingTop: '6px' }}>
                            Ghi chú: {event.extra}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 28px', borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', justifyContent: 'flex-end', background: 'rgba(0,0,0,0.2)',
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 20px', borderRadius: '10px', background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.1)', color: '#f8fafc', fontWeight: 600,
              fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
          >
            Đóng
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
